import { useParams, useRouteNavigator } from '@vkontakte/vk-mini-apps-router';
import type { NavIdProps } from '@vkontakte/vkui';
import { Fragment, useCallback, useState } from 'react';
import { errorMessage } from '../../api/client';
import { DORM_LABELS, CHARACTER_KIND_LABELS, STUDY_CLASS_LABELS } from '../../api/types';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { docNumber } from '../../shared/lib/docNumber';
import { fetchText } from '../../shared/lib/media';
import { copyText } from '../../shared/lib/vkBridge';
import {
  Action,
  ActionRow,
  ChipRow,
  CountTile,
  CountTileRow,
  EmptyState,
  ErrorState,
  FactGrid,
  Gap,
  Gutter,
  Hero,
  LoadingState,
  Prose,
  ReadMore,
  Screen,
  Section,
  SectionTitle,
  Tag,
  toast,
  type Fact,
} from '../../shared/ui';
import {
  displayName,
  useCharacterCard,
  type CardForm,
  type CharacterCardResponse,
} from './characterCard';

/**
 * Подписи ЧИТАТЕЛЯ, а не анкеты.
 *
 * FORM_FIELD_LABELS — дословные формулировки вопросов, по ним сверяют старые
 * анкеты, и трогать их нельзя. Но в карточке вопроса никто не задаёт: здесь
 * подпись только называет, что перед глазами, и в холсте она короткая —
 * «ХАРАКТЕР» вместо «Характер, ценности и моральные отклонения». Длинная
 * уезжала за правый край вместе с лучом раздела.
 */
const CARD_LABELS = {
  gender: t('registry.field.gender'),
  age: t('registry.field.age'),
  molesCount: t('registry.field.molesCount'),
  nicknames: t('registry.field.nicknames'),
  nameEn: t('registry.field.nameEn'),
  familyText: t('registry.field.familyText'),
  appearanceText: t('registry.field.appearanceText'),
  personalityText: t('registry.field.personalityText'),
  biographyText: t('registry.field.biographyText'),
} as const;

export function CharacterCardPanel({ id }: NavIdProps) {
  const routeNavigator = useRouteNavigator();
  const back = useBack();
  const params = useParams<'characterId'>();
  const characterId = Number(params?.characterId);

  const { card, error, loading, retry } = useCharacterCard(characterId);
  const [copying, setCopying] = useState(false);

  const showMessage = useCallback((message: string) => {
    toast(message);
  }, []);

  /**
   * Текст анкеты собирает сервер: тот же формат, что и в кабинете игрока,
   * и одно место, где он живёт. Копируем через мост — в вебвью ВК это
   * единственный надёжный способ попасть в буфер обмена.
   */
  const copyForm = useCallback(async () => {
    setCopying(true);
    try {
      const text = await fetchText(`/api/registry/${characterId}/export`);
      await copyText(text);
      showMessage(t('registry.card.copied'));
    } catch (cause: unknown) {
      showMessage(errorMessage(cause));
    } finally {
      setCopying(false);
    }
  }, [characterId, showMessage]);

  const title = card ? displayName(card.form) : t('registry.card.title');

  return (
    <Screen id={id}>
      {/* В шапке постоянная подпись, имя — крупным заголовком под портретом:
          разряженный Unbounded на 375 px обрезает длинное имя многоточием, а
          два одинаковых заголовка подряд читаются как ошибка вёрстки. */}
      {loading ? <LoadingState text={t('registry.loading.card')} /> : null}

      {!loading && !card ? (
        <ErrorState message={error} title={t('registry.error.cardFailed')} onRetry={retry} />
      ) : null}

      {card ? (
        <>
          <Dossier
            card={card}
            title={title}
            copying={copying}
            onCopy={() => {
              void copyForm();
            }}
            onBack={back}
            onOpenSection={(section) => routeNavigator.push(`/character/${characterId}/${section}`)}
          />
        </>
      ) : null}
    </Screen>
  );
}

/**
 * Шапка карточки: портрет, имя и три ярлыка. Действий здесь нет.
 *
 * Портрет идёт во всю ширину и без обрезки — прежняя миниатюра на 112 px
 * оставляла от кадра квадратик в углу, а приходят сюда смотреть внешность.
 * Заглушки на его месте нет: пустая рамка в полэкрана говорит только то, что
 * фото не приложили.
 *
 * Кнопки уехали вниз, к плиткам перехода: в макете шапка — это опознание
 * персонажа, и ряд кнопок между именем и первым разделом анкеты разрывал его
 * пополам ещё до того, как игрок прочёл хоть строчку.
 */
function Dossier({
  card,
  title,
  copying,
  onCopy,
  onBack,
  onOpenSection,
}: {
  card: CharacterCardResponse;
  title: string;
  copying: boolean;
  onCopy: () => void;
  onBack: () => void;
  onOpenSection: (section: 'items' | 'pets' | 'abilities') => void;
}) {
  // Раса стоит в шапке, а не в сетке фактов: это то же, чем персонаж
  // представляется, что общежитие и класс, и повторять её дважды незачем.
  const race = card.form.race ?? '';
  const hasTags = card.dorm !== null || card.studyClass !== null || race !== '';

  return (
    /*
      Досье — ОДНА колонка, как в холсте: кадр во всю ширину, под ним раздел с
      полем 16 px по бокам. Ритм держит сам раздел, поле — самый внешний его
      владелец; стопка из четырёх секций складывала поля Group и Div, и между
      чипами и подписью «Анкета» набегало 56 px вместо двадцати.
    */
    <>
      {/* Кадр от верхнего края, возврат поверх него — как в холсте. Он стоит
          ДО первого раздела: своего поля у него нет, а раздел отбил бы его
          сверху, и над фотографией просвечивала бы полоса фона. */}
      <Hero
        image={card.form.appearanceImage}
        caption={t('registry.card.caption', {
          kind: CHARACTER_KIND_LABELS[card.kind].toLocaleLowerCase('ru'),
          doc: docNumber(card.id),
        })}
        title={title}
        onBack={onBack}
      />

      <Section>
        {hasTags ? (
          <Gutter>
            {/* Три формы одного чипа, и разница в них смысловая: общежитие
                залито — это принадлежность, класс обведён малиновым — это
                ранг, раса стоит нейтральной рамкой — это просто описание. */}
            <ChipRow>
              {card.dorm === null ? null : (
                <Tag tone="success" size="l" fill>
                  {DORM_LABELS[card.dorm]}
                </Tag>
              )}
              {card.studyClass === null ? null : (
                <Tag tone="error" size="l">
                  {t('registry.card.studyClass', { value: STUDY_CLASS_LABELS[card.studyClass] })}
                </Tag>
              )}
              {race === '' ? null : <Tag size="l">{race}</Tag>}
            </ChipRow>
          </Gutter>
        ) : null}

        <FormBody form={card.form} />

        <Gutter>
          <Gap step="s5" />
          <CountTileRow>
            {/* Плитки ведут на свои экраны, как в холсте: досье заканчивается
              ими, а содержимое разделов живёт отдельно. */}
            <CountTile
              label={t('registry.tile.items')}
              count={card.items.length}
              onOpen={() => onOpenSection('items')}
            />
            <CountTile
              label={t('registry.tile.pets')}
              count={card.pets.length}
              onOpen={() => onOpenSection('pets')}
            />
            <CountTile
              label={t('registry.tile.abilities')}
              count={card.abilities.active.length + card.abilities.archive.length}
              onOpen={() => onOpenSection('abilities')}
            />
          </CountTileRow>

          <Gap step="s3" />
          <ActionRow>
            <Action tone="accent" size="m" stretched loading={copying} onClick={onCopy}>
              {t('registry.action.copyForm')}
            </Action>
            {card.player ? (
              <Action
                tone="quiet"
                size="m"
                stretched
                href={card.player.profileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('registry.action.playerProfile')}
              </Action>
            ) : null}
          </ActionRow>
        </Gutter>
      </Section>
    </>
  );
}

/** Малиновый луч — характеру и биографии, остальному бирюзовый: на экране
 *  второй акцент должен быть редким. */
interface TextSection {
  label: string;
  value: string | null;
  accent: 'cyan' | 'pink';
}

function FormBody({ form }: { form: CardForm }) {
  // Короткое значение в блоке во всю ширину выглядит как ошибка вёрстки,
  // поэтому обе колонки забирает только длинный хвост — ФИО по-английски, а
  // остальное идёт ПАРАМИ: пол с возрастом, прозвища с родинками. Парных
  // обязано быть чётное число — сетка в две колонки, и лишний остаётся в ряду
  // один, с дырой справа. Широкий факт стоит между парами, а не в конце:
  // так он делит их, а не оставляет хвостом.
  // Расы здесь нет: она ушла в ярлыки шапки.
  const facts: Fact[] = [
    { label: CARD_LABELS.gender, value: form.gender },
    { label: CARD_LABELS.age, value: form.age },
    { label: CARD_LABELS.nameEn, value: form.nameEn, wide: true },
    { label: CARD_LABELS.nicknames, value: form.nicknames },
    { label: CARD_LABELS.molesCount, value: form.molesCount },
  ];

  // Порядок как в анкете: семья идёт до внешности, характер и биография следом.
  const sections: TextSection[] = [
    { label: CARD_LABELS.familyText, value: form.familyText, accent: 'cyan' },
    { label: CARD_LABELS.appearanceText, value: form.appearanceText, accent: 'cyan' },
    { label: CARD_LABELS.personalityText, value: form.personalityText, accent: 'pink' },
    { label: CARD_LABELS.biographyText, value: form.biographyText, accent: 'pink' },
  ];

  const filled = sections.filter((section) => (section.value ?? '').trim() !== '');
  const hasFacts = facts.some((fact) => (fact.value ?? '') !== '');

  if (!hasFacts && filled.length === 0) {
    return <EmptyState title={t('registry.empty.form')}>{t('registry.empty.formHint')}</EmptyState>;
  }

  // Отступы взяты из холста: подпись раздела через 20 px от предыдущего блока,
  // содержимое — через 12 от подписи.
  return (
    <>
      {hasFacts ? (
        <>
          <SectionTitle>{t('common.section.form')}</SectionTitle>
          <Gutter>
            <FactGrid facts={facts} />
          </Gutter>
        </>
      ) : null}

      {filled.map((section) => (
        <Fragment key={section.label}>
          <SectionTitle accent={section.accent}>{section.label}</SectionTitle>
          <Gutter>
            {/* Свёрнуто до трёх строк: биография бывает на целый экран, и
                развёрнутая она отодвигает всё досье вниз. */}
            <ReadMore>
              <Prose>{section.value ?? ''}</Prose>
            </ReadMore>
          </Gutter>
        </Fragment>
      ))}
    </>
  );
}
